import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { supabase } from '../lib/supabase'
import { confirmDialog } from '../lib/confirmDialog'
import { useAuth } from '../hooks/useAuth'
import { useTeam } from '../hooks/useTeam'
import Container from '../components/common/Container'
import Card from '../components/common/Card'
import Button from '../components/common/Button'
import Select from '../components/common/Select'
import Loading from '../components/common/Loading'
import UpgradePrompt from '../components/common/UpgradePrompt'

export default function SnackScheduleManager() {
  
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { team, loading: teamLoading } = useTeam()
  
  const [snackSchedule, setSnackSchedule] = useState([])
  const [events, setEvents] = useState([])
  const [parents, setParents] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingAssignment, setEditingAssignment] = useState(null)
  const [formData, setFormData] = useState({
    event_id: '',
    parent_id: ''
  })

  // Redirect if not logged in or no team
  useEffect(() => {
    
    if (!authLoading && !teamLoading) {
      if (!user) {
        navigate('/auth')
      } else if (!team) {
        navigate('/team-setup')
      }
    }
  }, [user, team, authLoading, teamLoading, navigate])

  // Fetch snack schedule, events, and parents
  useEffect(() => {
    if (team?.id) {
      fetchSnackSchedule()
      fetchEvents()
      fetchParents()
    }
  }, [team?.id])

  const fetchSnackSchedule = async () => {
    setLoading(true)
    
    try {
      const { data, error } = await supabase
        .from('snack_schedule')
        .select('*, calendar_events(title, start_time, location), parents(email)')
        .eq('team_id', team.id)
        .order('created_at')

      if (error) throw error

      setSnackSchedule(data || [])
    } catch (err) {
      toast.error('Failed to load snack schedule')
    } finally {
      setLoading(false)
    }
  }

  const fetchEvents = async () => {
    
    try {
      const { data, error } = await supabase
        .from('calendar_events')
        .select('id, title, start_time, location')
        .eq('team_id', team.id)
        .gte('start_time', new Date().toISOString())
        .order('start_time')
        .limit(50)

      if (error) throw error

      setEvents(data || [])
    } catch (err) {
    }
  }

  const fetchParents = async () => {
    
    try {
      const { data, error } = await supabase
        .from('parents')
        .select('id, email')
        .eq('team_id', team.id)
        .order('email')

      if (error) throw error

      setParents(data || [])
    } catch (err) {
    }
  }

  const handleOpenForm = (assignment = null) => {
    
    if (assignment) {
      setEditingAssignment(assignment)
      setFormData({
        event_id: assignment.event_id || '',
        parent_id: assignment.parent_id || ''
      })
    } else {
      setEditingAssignment(null)
      setFormData({
        event_id: '',
        parent_id: ''
      })
    }
    
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingAssignment(null)
    setFormData({
      event_id: '',
      parent_id: ''
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.event_id && !formData.parent_id) {
      toast.error('Please select at least an event or parent')
      return
    }

    try {
      if (editingAssignment) {
        // Update existing assignment
        const { error } = await supabase
          .from('snack_schedule')
          .update({
            event_id: formData.event_id || null,
            parent_id: formData.parent_id || null
          })
          .eq('id', editingAssignment.id)
          .eq('team_id', team.id)

        if (error) throw error

      } else {
        // Create new assignment
        const { error } = await supabase
          .from('snack_schedule')
          .insert({
            team_id: team.id,
            event_id: formData.event_id || null,
            parent_id: formData.parent_id || null,
            completed: false
          })

        if (error) throw error

      }

      handleCloseForm()
      fetchSnackSchedule()
    } catch (err) {
      toast.error('Failed to save snack assignment')
    }
  }

  const handleToggleComplete = async (assignmentId, currentStatus) => {
    
    try {
      const { error } = await supabase
        .from('snack_schedule')
        .update({ completed: !currentStatus })
        .eq('id', assignmentId)
        .eq('team_id', team.id)

      if (error) throw error

      setSnackSchedule(snackSchedule.map(item =>
        item.id === assignmentId
          ? { ...item, completed: !currentStatus }
          : item
      ))
    } catch (err) {
      toast.error('Failed to update assignment')
    }
  }

  const handleDelete = async (assignmentId) => {
    const confirmed = await confirmDialog(
      'Delete this snack assignment?',
      { confirmText: 'Delete', cancelText: 'Cancel' }
    )
    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('snack_schedule')
        .delete()
        .eq('id', assignmentId)
        .eq('team_id', team.id)

      if (error) throw error

      fetchSnackSchedule()
    } catch (err) {
      toast.error('Failed to delete assignment')
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBD'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  if (authLoading || teamLoading || !team) {
    return <Loading />
  }

  // Check if team has paid tier
  if (team.season_tier !== 'paid') {
    return (
      <Container>
        <div className="max-w-4xl mx-auto py-8">
          <div className="mb-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="mb-4"
            >
              ← Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-brand-green">Snack Schedule</h1>
          </div>
          
          <UpgradePrompt
            title="Snack Schedule is a Pro Feature"
            features={[
              'Assign parents to bring snacks for games/practices',
              'Link assignments to calendar events',
              'Track completion status',
              'Private by default (not shown on public portal)',
              'Automated reminder emails (coming soon)'
            ]}
            tier="paid"
          />
        </div>
      </Container>
    )
  }

  return (
    <Container>
      <div className="max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            ← Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-brand-green">Snack Schedule</h1>
              <p className="text-gray-600 mt-2">
                Assign parents to bring snacks. Private by default.
              </p>
            </div>
            <Button onClick={() => handleOpenForm()}>
              + Add Assignment
            </Button>
          </div>
        </div>

        {/* Privacy Notice */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🔒</span>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">
                Private by Default
              </h3>
              <p className="text-sm text-gray-700">
                Snack schedules are private and not shown on the public portal by default. 
                You can control this in Portal Settings if you want to make it public.
              </p>
            </div>
          </div>
        </Card>

        {/* Snack Schedule List */}
        {loading ? (
          <Loading />
        ) : snackSchedule.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <span className="text-6xl mb-4 block">🍎</span>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No Snack Assignments Yet
              </h3>
              <p className="text-gray-600 mb-6">
                Start organizing who brings snacks to each game or practice.
              </p>
              <Button onClick={() => handleOpenForm()}>
                + Add First Assignment
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {snackSchedule.map((assignment) => (
              <Card key={assignment.id} className={`hover:shadow-md transition-shadow ${assignment.completed ? 'bg-gray-50' : ''}`}>
                <div className="flex items-center gap-4">
                  {/* Checkbox */}
                  <button
                    onClick={() => handleToggleComplete(assignment.id, assignment.completed)}
                    className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                      assignment.completed
                        ? 'bg-brand-green border-brand-green'
                        : 'border-gray-300 hover:border-brand-green'
                    }`}
                  >
                    {assignment.completed && (
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>

                  {/* Assignment Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-4 flex-wrap">
                      {assignment.calendar_events ? (
                        <div>
                          <h3 className={`font-semibold ${assignment.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                            {assignment.calendar_events.title}
                          </h3>
                          <p className="text-sm text-gray-500">
                            📅 {formatDate(assignment.calendar_events.start_time)}
                            {assignment.calendar_events.location && ` • 📍 ${assignment.calendar_events.location}`}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <h3 className="font-semibold text-gray-500 italic">
                            No event linked
                          </h3>
                        </div>
                      )}
                      
                      {assignment.parents?.email && (
                        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded">
                          <span className="text-sm font-medium text-gray-900">
                            👤 {assignment.parents.email}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenForm(assignment)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(assignment.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full">
              <h2 className="text-2xl font-bold text-brand-green mb-6">
                {editingAssignment ? 'Edit Snack Assignment' : 'Add Snack Assignment'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Event
                  </label>
                  <Select
                    value={formData.event_id}
                    onChange={(e) => setFormData({ ...formData, event_id: e.target.value })}
                  >
                    <option value="">Select an event (optional)</option>
                    {events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.title} - {formatDate(event.start_time)}
                      </option>
                    ))}
                  </Select>
                  {events.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      No upcoming events found. Import your calendar in Settings.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assigned Parent
                  </label>
                  <Select
                    value={formData.parent_id}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  >
                    <option value="">Select a parent (optional)</option>
                    {parents.map((parent) => (
                      <option key={parent.id} value={parent.id}>
                        {parent.email}
                      </option>
                    ))}
                  </Select>
                  {parents.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      No parents found. Add parents in Parent Manager.
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1">
                    {editingAssignment ? 'Save Changes' : 'Add Assignment'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={handleCloseForm}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}
      </div>
    </Container>
  )
}

